/**
 * Pure helpers for converting between ADF (ProseMirror) docs and markdown
 * source. Used by `MarkdownSourceView` to populate its textarea on mount and
 * to round-trip the user's edits back to ADF on unmount.
 *
 * Both functions are intentionally side-effect free and React-free so they can
 * be unit-tested in isolation. Errors from the markdown parser are *thrown* —
 * `markdownToAdf` is a "throws on parse error" boundary; the calling component
 * is responsible for catching and surfacing them.
 *
 * Both accept an optional `trace` hook (default off) so the orchestrator can
 * observe each conversion — including the intermediate mdast tree and any parse
 * error — without these helpers importing the tracer singleton. Purity is
 * preserved: the hook is supplied by the caller, and nothing extra is computed
 * when it is absent. See `docs/devtools-tracing-design.md` §3–4.
 *
 * Ticket: https://hello.jira.atlassian.cloud/browse/EDITOR-6558
 */
import { fromMarkdown } from 'mdast-util-from-markdown';
import { gfmStrikethroughFromMarkdown } from 'mdast-util-gfm-strikethrough';
import { gfmTableFromMarkdown } from 'mdast-util-gfm-table';
import { gfmTaskListItemFromMarkdown } from 'mdast-util-gfm-task-list-item';
import { gfmStrikethrough } from 'micromark-extension-gfm-strikethrough';
import { gfmTable } from 'micromark-extension-gfm-table';
import { gfmTaskListItem } from 'micromark-extension-gfm-task-list-item';

import type { DocNode } from '@atlaskit/adf-schema';
import type { Node as PMNode, Schema } from '@atlaskit/editor-prosemirror/model';
import { fg } from '@atlaskit/platform-feature-flags';
import { fromProseMirrorWithAnnotations } from '@atlassian/mdast-util-from-pm';
import { EMPTY_TASK_LIST_PLACEHOLDER } from '@atlassian/mdast-util-from-pm/empty-task-list-placeholder';
import { fromPMHandlers as defaultHandlers } from '@atlassian/mdast-util-from-pm/handlers';
import {
	createMediaHandlers,
	type SerializeFileMedia,
} from '@atlassian/mdast-util-from-pm/handlers/media';
import type { MarkdownSourceAnnotationRange } from '@atlassian/mdast-util-from-pm/types';
import { toProseMirror } from '@atlassian/mdast-util-to-pm';
import { handlers as toPmHandlers } from '@atlassian/mdast-util-to-pm/handlers';

import type { ConvertTraceHook, ConvertTracePayload, MdastRoot } from './md-mode-tracer-types';

/**
 * Optional dependencies the markdown conversion pipeline needs in order to
 * handle product-specific concerns. Today this is only `serializeFileMedia`
 * (Confluence-supplied proxy URL builder for `media (type: "file")` nodes),
 * but more injectors can be added here without breaking the existing call
 * sites — every field is optional and the default behaviour is "do the
 * platform-agnostic thing."
 *
 * Why injection rather than a global helper? `mdast-util-from-pm` lives in
 * `platform/` and must remain product-agnostic — it must not know about
 * Confluence's `/wiki/download/...` proxy or about content-id scoping. The
 * Confluence wrapper around `MarkdownSourceView` is responsible for
 * supplying a serialiser that builds the right URL for the current page.
 */
export type MarkdownConversionOptions = {
	/**
	 * Builds a public, viewer-resolvable URL for a `media (type: "file")`
	 * node so it can be embedded as a CommonMark image. Implementations
	 * should return a URL that authenticates via the viewer's existing
	 * session (e.g. Confluence's `/wiki/download/attachments/...` proxy)
	 * — never a media token, signed download URL, or anything that would
	 * leak credentials into the persisted markdown.
	 *
	 * Returning `undefined` (or omitting this entirely) causes file media
	 * to be serialised as alt-text-only, which is safe but lossy.
	 */
	serializeFileMedia?: SerializeFileMedia;
	/**
	 * Optional observer invoked once with the conversion result (or error).
	 * Supplied by the orchestrator only when tracing is enabled; omitted in
	 * tests and in the GA path, keeping these helpers pure and zero-cost.
	 */
	trace?: ConvertTraceHook;
};

/**
 * The result of converting ADF to markdown.
 *
 * `annotations` contains character ranges for each annotation mark (inline
 * comment) in the source document — suitable for use as CM6 decoration ranges
 * or for re-applying annotation marks on commit via `applyAnnotationRanges`.
 */
export type AdfToMarkdownResult = {
	annotations: MarkdownSourceAnnotationRange[];
	markdown: string;
};

const emptyTaskListItemPattern = /^(\s*)([-*+]|\d+\.) \[([ xX])\](\r?)$/u;
const fencePattern = /^\s*(`{3,}|~{3,})(.*)$/u;

type NormalizedEmptyTaskListItems = {
	didNormalize: boolean;
	markdown: string;
};

/**
 * `mdast-util-gfm-task-list-item` only treats `[ ]` / `[x]` as a task marker
 * when there is trailing item content. Users can still create an empty task
 * item in source view as `* [ ]`; normalize that to the equivalent form with
 * invisible placeholder content before parsing so it round-trips as a task item
 * instead of a bullet item containing literal bracket text. The placeholder is
 * stripped from the parsed ProseMirror doc immediately after parsing.
 */
const normalizeEmptyTaskListItems = (markdown: string): NormalizedEmptyTaskListItems => {
	let openFence: { character: string; length: number } | undefined;
	let didNormalize = false;
	const lines = markdown.split('\n');
	const normalizedLines: string[] = [];

	for (const line of lines) {
		const fenceMatch = fencePattern.exec(line);
		const fence = fenceMatch?.[1];
		if (fence) {
			if (!openFence) {
				openFence = { character: fence[0], length: fence.length };
				normalizedLines.push(line);
				continue;
			}
			const fenceSuffix = fenceMatch?.[2] ?? '';
			if (
				fence[0] === openFence.character &&
				fence.length >= openFence.length &&
				fenceSuffix.trim().length === 0
			) {
				openFence = undefined;
			}
			normalizedLines.push(line);
			continue;
		}

		if (openFence) {
			normalizedLines.push(line);
			continue;
		}

		const match = emptyTaskListItemPattern.exec(line);
		const normalizedLine = match
			? `${match[1]}${match[2]} [${match[3]}] ${EMPTY_TASK_LIST_PLACEHOLDER}${match[4]}`
			: line;
		didNormalize ||= Boolean(match);
		normalizedLines.push(normalizedLine);
	}

	return {
		didNormalize,
		markdown: didNormalize ? normalizedLines.join('\n') : markdown,
	};
};

const removeEmptyTaskListPlaceholders = (doc: PMNode, schema: Schema): PMNode => {
	const strip = (node: unknown): unknown => {
		if (!node || typeof node !== 'object') {
			return node;
		}
		const json = node as { content?: unknown[]; text?: string; type?: string };
		if (json.type === 'text' && json.text !== undefined) {
			if (json.text === EMPTY_TASK_LIST_PLACEHOLDER) {
				return null;
			}
			return json;
		}
		if (Array.isArray(json.content)) {
			return {
				...json,
				content: json.content.map(strip).filter(Boolean),
			};
		}
		return json;
	};
	return schema.nodeFromJSON(strip(doc.toJSON()));
};

const markdownToAdfViaMdast = (
	markdown: string,
	schema: Schema,
	options?: { onMdast?: (mdast: MdastRoot) => void; stripEmptyTaskListPlaceholders?: boolean },
): PMNode => {
	const mdast = fromMarkdown(markdown, {
		extensions: [gfmStrikethrough(), gfmTable(), gfmTaskListItem()],
		mdastExtensions: [
			gfmStrikethroughFromMarkdown(),
			gfmTableFromMarkdown(),
			gfmTaskListItemFromMarkdown(),
		],
	});
	// Hand the intermediate tree to the tracer before it is discarded (point 1).
	options?.onMdast?.(mdast);
	const doc = toProseMirror(schema, toPmHandlers)(mdast);
	if (!doc) {
		throw new Error('[markdownToAdf] mdast-util-to-pm returned null for the given markdown');
	}
	return options?.stripEmptyTaskListPlaceholders
		? removeEmptyTaskListPlaceholders(doc, schema)
		: doc;
};

const toTraceError = (err: unknown): { message: string; stack?: string } => ({
	message: err instanceof Error ? err.message : String(err),
	stack: err instanceof Error ? err.stack : undefined,
});

/**
 * Builds and delivers a conversion trace payload inside a guard. Tracing is
 * observe-only: a serialization or recorder failure must never propagate into
 * the conversion path. Without this the surrounding try/catch would rethrow the
 * tracer's error, and a caller would misread a *successful* conversion as a
 * parse failure (dropping the user's edit / snapping content back).
 */
const emitConvertTrace = (
	trace: ConvertTraceHook | undefined,
	build: () => ConvertTracePayload,
): void => {
	if (!trace) {
		return;
	}
	try {
		trace(build());
	} catch {
		// Swallow — tracing must not affect conversion behaviour.
	}
};

/**
 * Convert a live ProseMirror document (or its ADF JSON representation) to a
 * markdown string plus annotation ranges.
 *
 * Uses `fromProseMirrorWithAnnotations` which returns both the clean markdown
 * string and an array of annotation mark character ranges.
 *
 * Pass `options.serializeFileMedia` from the Confluence wrapper to make
 * `media (type: "file")` nodes round-trip via the tenant attachment proxy.
 * Without it, file media is dropped (alt-text-only).
 */
export const adfToMarkdown = (
	doc: PMNode,
	schema: Schema,
	options: MarkdownConversionOptions = {},
): AdfToMarkdownResult => {
	const handlers = options.serializeFileMedia
		? {
				...defaultHandlers,
				...createMediaHandlers({ serializeFileMedia: options.serializeFileMedia }),
			}
		: defaultHandlers;

	const { trace } = options;
	const start = trace ? performance.now() : 0;
	try {
		const result = fromProseMirrorWithAnnotations(schema, handlers)(doc);
		emitConvertTrace(trace, () => ({
			direction: 'adf->md',
			input: { adf: doc.toJSON() as DocNode },
			output: { markdown: result.markdown, annotations: result.annotations },
			durationMs: performance.now() - start,
		}));
		return result;
	} catch (err) {
		emitConvertTrace(trace, () => ({
			direction: 'adf->md',
			input: { adf: doc.toJSON() as DocNode },
			output: {},
			durationMs: performance.now() - start,
			error: toTraceError(err),
		}));
		throw err;
	}
};

/**
 * Parse a markdown string back into a ProseMirror document using the supplied
 * schema. Throws if the markdown contains constructs the schema cannot
 * represent — callers must wrap this in try/catch and decide what to do
 * (e.g. discard the change, surface a flag, etc.).
 *
 * Uses `mdast-util-to-pm` (our own package). The mdast-util path is needed for
 * annotation round-trips and produces equivalent output for all other node
 * types.
 */
export const markdownToAdf = (
	markdown: string,
	schema: Schema,
	options: { trace?: ConvertTraceHook } = {},
): PMNode => {
	const { trace } = options;
	const start = trace ? performance.now() : 0;
	let capturedMdast: MdastRoot | undefined;
	try {
		const normalized = fg('platform_editor_fix_md_empty_list_roundtrip')
			? normalizeEmptyTaskListItems(markdown)
			: { didNormalize: false, markdown };
		const doc = markdownToAdfViaMdast(normalized.markdown, schema, {
			stripEmptyTaskListPlaceholders: normalized.didNormalize,
			onMdast: trace
				? (mdast) => {
						capturedMdast = mdast;
					}
				: undefined,
		});
		emitConvertTrace(trace, () => ({
			direction: 'md->adf',
			input: { markdown },
			mdast: capturedMdast,
			output: { adf: doc.toJSON() as DocNode },
			durationMs: performance.now() - start,
		}));
		return doc;
	} catch (err) {
		emitConvertTrace(trace, () => ({
			direction: 'md->adf',
			input: { markdown },
			mdast: capturedMdast,
			output: {},
			durationMs: performance.now() - start,
			error: toTraceError(err),
		}));
		throw err;
	}
};
